import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getCognitoLoginUrl, getCognitoSignUpUrl } from '../config/aws';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import { GoogleG } from '../components/GoogleBrand';
import { Logo } from '../components/common';
import { Icon } from '../components/icons';
import { SIGNUP_PATH_KEY } from '../lib/signupPath';
import { track, useScreenView } from '../lib/analytics';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface AuthPageProps {
  t: Theme;
}

export default function Auth({ t }: AuthPageProps) {
  const { isAuthenticated, handleTokens } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<'main' | 'university'>('main');

  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  useScreenView(view === 'university' ? 'auth/university' : 'auth');

  if (view === 'university') return <UniversityEmail t={t} onBack={() => setView('main')} />;

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: t.paper, height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '0 28px 32px', position: 'relative' }}>
        {/* Orbs */}
        <div style={{
          position: 'absolute', top: '8%', right: '-15%', width: '75%', aspectRatio: '1',
          background: `radial-gradient(circle, ${t.blush}55 0%, ${t.terracotta}25 40%, transparent 70%)`,
          borderRadius: '50%', pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '35%', left: '-10%', width: '55%', aspectRatio: '1',
          background: `radial-gradient(circle, ${t.gold}28 0%, transparent 65%)`,
          borderRadius: '50%', pointerEvents: 'none',
        }} />

        {/* Logo centred in the upper half */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          <Logo t={t} size={1.6} />
        </div>

        <div style={{ position: 'relative' }}>
          <div style={{ marginBottom: 20 }} />

          <h1 style={{
            fontFamily: FONTS.display, fontStyle: 'italic', fontWeight: 400,
            fontSize: 48, margin: '0 0 16px', color: t.ink, letterSpacing: -0.8, lineHeight: 1,
          }}>
            Find your Person.<br />Find your People.
          </h1>

          <p style={{
            fontFamily: FONTS.sans, fontSize: 15, lineHeight: 1.5,
            color: t.inkSoft, margin: '0 0 36px', maxWidth: 300,
          }}>
            For people who've accepted that their weekend revolves around transport zones.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button
              onClick={() => { track('login_start', { method: 'google' }, { beacon: true }); window.location.href = getCognitoLoginUrl('Google'); }}
              style={{
                all: 'unset', cursor: 'pointer',
                padding: '16px 22px', borderRadius: 100, boxSizing: 'border-box',
                background: t.ink, color: t.paper,
                fontFamily: FONTS.sans, fontSize: 15, fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                width: '100%',
              }}
            >
              <GoogleG size={18} />
              Continue with Google
            </button>

            <button
              data-testid="auth-university"
              onClick={() => setView('university')}
              style={{
                all: 'unset', cursor: 'pointer',
                padding: '15px 22px', borderRadius: 100, boxSizing: 'border-box',
                background: t.card, color: t.ink,
                border: `1px solid ${t.terracotta}66`,
                fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
                width: '100%',
              }}
            >
              {Icon.cap({ s: 18, c: t.terracotta })}
              Continue with university email
            </button>

            <button
              onClick={() => { track('login_start', { method: 'email' }, { beacon: true }); window.location.href = getCognitoLoginUrl(); }}
              style={{
                all: 'unset', cursor: 'pointer',
                padding: '14px 22px', borderRadius: 100, boxSizing: 'border-box',
                background: 'transparent', color: t.inkSoft,
                fontFamily: FONTS.sans, fontSize: 13, fontWeight: 500,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '100%',
                border: `1px dashed ${t.line}`,
              }}
            >
              Continue with email
            </button>

            {import.meta.env.DEV && (
              <button
                onClick={() => handleTokens('mock.seed-priya-0001-0001-0001-000000000001', 'mock', 'mock')}
                style={{
                  all: 'unset', cursor: 'pointer',
                  padding: '14px 22px', borderRadius: 100, boxSizing: 'border-box',
                  background: t.terracotta, color: t.paper,
                  fontFamily: FONTS.sans, fontSize: 13, fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '100%',
                }}
              >
                [DEV] Bypass Login (Priya)
              </button>
            )}
          </div>

          <p style={{
            fontFamily: FONTS.sans, fontSize: 11.5, color: t.inkMute,
            textAlign: 'center', marginTop: 20, lineHeight: 1.5,
          }}>
            By continuing you agree to our Terms and Privacy Policy.
            <br />We won't post without asking. Obviously.
          </p>
        </div>
      </div>

      {/* Perks strip */}
      <div style={{
        padding: '16px 20px 32px', borderTop: `0.5px solid ${t.line}`,
        background: t.paperAlt,
        display: 'flex', justifyContent: 'space-around',
        paddingBottom: 'max(32px, env(safe-area-inset-bottom))',
      }}>
        {[
          { icon: Icon.pin({ s: 14, c: t.terracotta }), label: 'Starting in London' },
          { icon: Icon.sparkle({ s: 14, c: t.terracotta }), label: 'AI date planner' },
          { icon: Icon.heart({ s: 14, c: t.terracotta }), label: 'Real people' },
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            {item.icon}
            <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: t.inkSoft, fontWeight: 500 }}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── University email ────────────────────────────────────────────
// Checks the address against the university allowlist (server-side, so the
// list lives in one place), then hands off to Cognito's own sign-up form.
// Cognito owns the password and emails a verification code; university status
// is recorded later, at registration, from that VERIFIED email — nothing on
// this screen is trusted on its own.

function UniversityEmail({ t, onBack }: { t: Theme; onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [recognised, setRecognised] = useState<{ name: string | null } | null>(null);

  const check = async () => {
    const value = email.trim();
    if (!value) { setError('Enter your university email.'); return; }
    setChecking(true); setError(''); setRecognised(null);
    try {
      const res = await fetch(`${API_BASE}/api/auth/university-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value }),
      });
      const data = await res.json();
      track('university_check', { result: data.isUniversity ? 'recognised' : 'rejected' });
      if (data.isUniversity) setRecognised({ name: data.name });
      else setError("That doesn't look like a university email. Use one ending in .ac.uk — or london.edu for LBS.");
    } catch {
      setError('Something went wrong — try again.');
    } finally {
      setChecking(false);
    }
  };

  const continueToSignUp = () => {
    try { sessionStorage.setItem(SIGNUP_PATH_KEY, 'university'); } catch { /* storage blocked — status still set server-side */ }
    track('login_start', { method: 'university' }, { beacon: true });
    window.location.href = getCognitoSignUpUrl();
  };

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: t.paper, height: '100%', padding: '16px 28px 32px', boxSizing: 'border-box', overflowY: 'auto' }}>
      <button onClick={onBack} aria-label="Back" style={{ all: 'unset', cursor: 'pointer', width: 38, height: 38, borderRadius: 100, background: t.card, border: `1px solid ${t.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {Icon.back({ s: 16, c: t.ink })}
      </button>

      <div style={{ marginTop: 40 }}>
        <div style={{ width: 56, height: 56, borderRadius: 100, background: `${t.terracotta}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 22 }}>
          {Icon.cap({ s: 26, c: t.terracotta })}
        </div>
        <h1 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontWeight: 400, fontSize: 36, margin: '0 0 10px', color: t.ink, letterSpacing: -0.5, lineHeight: 1.05 }}>
          Your university email
        </h1>
        <p style={{ fontFamily: FONTS.sans, fontSize: 14.5, lineHeight: 1.55, color: t.inkSoft, margin: '0 0 28px' }}>
          For students and staff at UK universities, and London Business School.
        </p>

        <input
          data-testid="uni-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          autoFocus
          value={email}
          onChange={e => { setEmail(e.target.value); setRecognised(null); setError(''); }}
          onKeyDown={e => { if (e.key === 'Enter' && !recognised) check(); }}
          placeholder="you@university.ac.uk"
          style={{ width: '100%', boxSizing: 'border-box', padding: '15px 18px', borderRadius: 14, border: `1px solid ${error ? t.terracotta : t.line}`, background: t.card, color: t.ink, fontFamily: FONTS.sans, fontSize: 16, outline: 'none' }}
        />

        {error && (
          <div data-testid="uni-error" style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.terracotta, marginTop: 10, lineHeight: 1.45 }}>{error}</div>
        )}

        {recognised ? (
          <div data-testid="uni-recognised" style={{ marginTop: 18, padding: '16px 18px', borderRadius: 16, background: `${t.sage}18`, border: `1px solid ${t.sage}55` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: FONTS.sans, fontSize: 14.5, fontWeight: 600, color: t.ink }}>
              {Icon.check({ s: 16, c: t.sage, w: 2.4 })}
              {recognised.name ?? 'University email recognised'}
            </div>
            <div style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.inkSoft, marginTop: 8, lineHeight: 1.5 }}>
              Next, create your account using <strong>this same email</strong>. We'll send it a code to confirm it's yours.
            </div>
          </div>
        ) : null}

        <button
          data-testid={recognised ? 'uni-continue' : 'uni-check'}
          onClick={recognised ? continueToSignUp : (checking ? undefined : check)}
          style={{ all: 'unset', cursor: checking ? 'default' : 'pointer', marginTop: 22, width: '100%', boxSizing: 'border-box', textAlign: 'center', padding: '16px 22px', borderRadius: 100, background: t.ink, color: t.paper, fontFamily: FONTS.sans, fontSize: 15, fontWeight: 600, opacity: checking ? 0.6 : 1 }}
        >
          {recognised ? 'Continue to sign up' : checking ? 'Checking…' : 'Continue'}
        </button>

        <p style={{ fontFamily: FONTS.sans, fontSize: 11.5, color: t.inkMute, textAlign: 'center', marginTop: 20, lineHeight: 1.5 }}>
          Your university address is only used to confirm you're at a university.
        </p>
      </div>
    </div>
  );
}
